import { useState, useEffect } from 'react';
import { Zap, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'react-toastify';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { getSecuritySettings, updateSecuritySettings, type SecuritySettings } from '@/lib/adminApi';

const DEFAULT_SETTINGS: SecuritySettings = {
  autoBlockEnabled: true,
  autoBlockThreshold: 3,
  autoBlockDurationHours: 24,
  blockResponseCode: 403,
  blockResponseMessage: 'Access denied',
};

export function AutoBlockSettings() {
  const [settings, setSettings] = useState<SecuritySettings>(DEFAULT_SETTINGS);
  const [originalSettings, setOriginalSettings] = useState<SecuritySettings>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setIsLoading(true);
      const data = await getSecuritySettings();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error: unknown) {
      console.error('Failed to load security settings:', error);
      // Use defaults if settings don't exist yet
      setSettings(DEFAULT_SETTINGS);
      setOriginalSettings(DEFAULT_SETTINGS);
    } finally {
      setIsLoading(false);
    }
  };

  const hasChanges = () => {
    return JSON.stringify(settings) !== JSON.stringify(originalSettings);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const updated = await updateSecuritySettings(settings);
      setSettings(updated);
      setOriginalSettings(updated);
      toast.success('Auto-block settings saved');
    } catch (error: unknown) {
      console.error('Failed to save settings:', error);
      toast.error((error as Error)?.message || 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-12 bg-muted rounded" />
        <div className="h-12 bg-muted rounded" />
        <div className="h-12 bg-muted rounded" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-purple-400" />
          <h3 className="text-sm font-semibold text-white">Auto-Blocking</h3>
        </div>
        {hasChanges() && (
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving}
            className="h-8 bg-[#8FB560] hover:bg-[#7a9d51]"
          >
            <Save className="w-3 h-3 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Master Toggle */}
        <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
          <div>
            <h4 className="text-sm font-medium text-white">Enable Auto-Blocking</h4>
            <p className="text-xs text-muted-foreground">
              Automatically block IPs that trigger multiple security alerts.
            </p>
          </div>
          <Switch
            checked={settings.autoBlockEnabled}
            onCheckedChange={(checked) => setSettings({ ...settings, autoBlockEnabled: checked })}
          />
        </div>

        {/* Settings (only show when enabled) */}
        <div className={`space-y-4 ${!settings.autoBlockEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
          {/* Threshold */}
          <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-white">Block Threshold</h4>
              <p className="text-xs text-muted-foreground">
                Number of suspicious events before auto-blocking an IP.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={settings.autoBlockThreshold}
                onChange={(e) => setSettings({ ...settings, autoBlockThreshold: parseInt(e.target.value) || 1 })}
                className="bg-background border-border h-8 w-16 text-center"
                min={1}
                max={100}
                disabled={!settings.autoBlockEnabled}
              />
              <span className="text-sm text-muted-foreground">events</span>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-white">Block Duration</h4>
              <p className="text-xs text-muted-foreground">
                How long to automatically block suspicious IPs.
              </p>
            </div>
            <Select
              value={settings.autoBlockDurationHours.toString()}
              onValueChange={(val) => setSettings({ ...settings, autoBlockDurationHours: parseInt(val) })}
              disabled={!settings.autoBlockEnabled}
            >
              <SelectTrigger className="w-32 h-8 bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">1 hour</SelectItem>
                <SelectItem value="6">6 hours</SelectItem>
                <SelectItem value="24">24 hours</SelectItem>
                <SelectItem value="72">3 days</SelectItem>
                <SelectItem value="168">1 week</SelectItem>
                <SelectItem value="720">30 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Response Code */}
          <div className="flex items-center justify-between p-4 bg-background rounded-lg border border-border">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-white">Block Response Code</h4>
              <p className="text-xs text-muted-foreground">
                HTTP status code returned for blocked requests.
              </p>
            </div>
            <Select
              value={settings.blockResponseCode.toString()}
              onValueChange={(val) => setSettings({ ...settings, blockResponseCode: parseInt(val) })}
              disabled={!settings.autoBlockEnabled}
            >
              <SelectTrigger className="w-32 h-8 bg-background border-border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="403">403 Forbidden</SelectItem>
                <SelectItem value="429">429 Too Many</SelectItem>
                <SelectItem value="503">503 Unavailable</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Response Message */}
          <div className="p-4 bg-background rounded-lg border border-border">
            <div className="mb-2">
              <Label className="text-sm font-medium text-white">Block Message</Label>
              <p className="text-xs text-muted-foreground">
                Message shown to blocked users.
              </p>
            </div>
            <Input
              value={settings.blockResponseMessage}
              onChange={(e) => setSettings({ ...settings, blockResponseMessage: e.target.value })}
              className="bg-background border-border"
              placeholder="Access denied"
              disabled={!settings.autoBlockEnabled}
            />
          </div>
        </div>

        {/* Status Summary */}
        <div className={`p-3 rounded-lg ${
          settings.autoBlockEnabled
            ? 'bg-purple-500/10 border border-purple-500/20'
            : 'bg-muted border border-border'
        }`}>
          <p className="text-xs text-muted-foreground">
            {settings.autoBlockEnabled ? (
              <>
                <span className="text-purple-400 font-medium">Auto-blocking is active.</span>
                {' '}IPs will be blocked for {formatDuration(settings.autoBlockDurationHours)} after{' '}
                {settings.autoBlockThreshold} suspicious {settings.autoBlockThreshold === 1 ? 'event' : 'events'}.
              </>
            ) : (
              <>
                <span className="text-muted-foreground font-medium">Auto-blocking is disabled.</span>
                {' '}Suspicious IPs will be tracked but not automatically blocked.
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function formatDuration(hours: number): string {
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'}`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'}`;
  const weeks = Math.floor(days / 7);
  return `${weeks} week${weeks === 1 ? '' : 's'}`;
}
